// Configuración → Procesos: "Así quedaría tu agente" EN ESTE PROCESO (W2-5,
// reconstruida en F2-6a, acotada al proceso en F4-2).
//
// La tarjeta describía el agente ENTERO en medio de una pantalla que edita UN
// proceso: lo del agente (saludo, consentimiento, opciones base) y lo del
// proceso (sus etapas y sus preguntas) se leían como una sola pila. Desde F4-2
// esta tarjeta dibuja SÓLO el proceso que está seleccionado arriba, y lo que es
// del agente vive en la tira de arriba (`AgentStrip`), que lee esta misma
// consulta.
//
// Sin botón de ACTUALIZAR: la vista se vuelve a pedir sola cada vez que cambian
// los procesos, las etapas o la ficha, porque quien las guarda invalida
// `agent-compiled-preview`. Un botón para refrescar a mano sería pedirle al
// cliente que se acuerde de hacer lo que la pantalla ya hace.
//
// Y DESDE W4'-3b, EL BOTÓN QUE LA ESCRIBE. La ola 2 dibujaba lo que quedaría y
// decía, donde estaría el botón, que el botón no existía todavía; aquí existe.
// PUBLICAR es del AGENTE ENTERO y no de este proceso, aunque viva en la tarjeta
// que dibuja uno: un agente publicado a medias es un menú que ofrece opciones
// que llaman a flujos que no existen (D12).
//
// LA PANTALLA NO REDACTA NADA. Los impedimentos, el delta, el recibo y lo
// deshecho llegan en frases del servidor, que es el que escribe: si la pantalla
// compusiera las suyas, lo que el cliente lee y lo que se escribió podrían
// contar cosas distintas el primer día que difieran.
//
// DOS VISTAS del mismo proceso: la ESTRUCTURA (etapa por etapa, con lo que cada
// una exige, obtiene y pregunta) y una CONVERSACIÓN DE EJEMPLO armada sólo con
// las palabras que el agente tiene escritas. Ninguna de las dos redacta prosa:
// lo que cambia se dibuja como hechos con vocabulario fijo, porque un archivo de
// configuración no se lee como un mensaje.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type AgentDelta, type AgentTerminal } from '@vysto/shared/compiler'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import {
  getAgentPreview,
  getPublishPreview,
  publishAgent,
  type PublishAgentResult,
  type PreviewStage,
  undoPublish,
} from './agent-preview-data.js'
import { FactList } from './FactList.js'
import { DATA_TYPE_LABELS, type FieldDataType } from './fields-data.js'
import { STAGE_BEHAVIOR_CHIP, STAGE_BEHAVIOR_TONE } from './pipelines-copy.js'
import {
  Alert,
  Button,
  Chip,
  DaySeparator,
  MessageBubble,
  MessageThread,
  Modal,
  SystemEvent,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { relativeAgeEs } from '../../lib/relative-time.js'

/** Qué es esta tarjeta. La vista previa se MIRA: nada de lo que muestra está
 *  publicado todavía. */
const PREVIEW_INTRO =
  'Esto es lo que tu agente preguntaría si se armara con los procesos que tienes hoy.'
/** EL APPLY DEL CICLO (W4'-3b). Donde hasta ayer había una disculpa ("el botón
 *  para aplicarla llega en la etapa de publicación") hay un botón. Publica el
 *  agente ENTERO, no este proceso: por eso la etiqueta no lo nombra. */
const PUBLISH_LABEL = 'Publicar'
/** De qué canal cuelga el menú que se publicaría (D15). El servidor manda el
 *  nombre del canal ya armado; la pantalla sólo lo sienta en la oración. */
function publishIdentityLine(label: string): string {
  return `Se publica al canal ${label}.`
}
/** LA PUERTA A PROBAR (W4'-6c U2). La tarjeta enseña lo que quedaría y el
 *  Publicar lo escribe, pero probarlo vivía a dos pantallas sin ninguna puerta.
 *  Es un enlace DE VERDAD al editor de este agente: `?probar=1` es lo que
 *  `FlowEditorScreen` lee para abrir su simulador al llegar (el mismo panel del
 *  botón "Probar" de esa pantalla, no un segundo simulador). */
const TRY_LINK_LABEL = 'Probar el agente'
function tryHref(botId: string): string {
  return `/asistente/${encodeURIComponent(botId)}/flujos?probar=1`
}
/** El título del diálogo, y lo que se está mirando dentro de él: primero lo que
 *  CAMBIARÍA, después lo que QUEDÓ. El tiempo verbal vive en los encabezados,
 *  nunca en los renglones: un hecho no se conjuga. */
const PUBLISH_DIALOG_TITLE = 'Publicar el agente'
const PUBLISH_CONFIRM_LABEL = 'Publicar agente'
const PUBLISH_DELTA_FUTURE_HEADING = 'Qué cambiaría'
const PUBLISH_DELTA_PAST_HEADING = 'Qué cambió'
const PUBLISH_RECEIPT_HEADING = 'Así quedó tu agente'
/** Lo que deshacer hace, dicho ANTES de ofrecerlo: no es un borrón, es volver a
 *  publicar el agente anterior como una publicación más. */
const PUBLISH_UNDO_HINT =
  'Deshacer vuelve a publicar la versión anterior de tu agente. Las conversaciones que ya iban ' +
  'corriendo terminan como empezaron.'
/** La puerta del Publicar no cargó: el botón está apagado por falta de datos,
 *  no por un impedimento del agente, y eso también se dice. */
const PUBLISH_PREVIEW_ERROR = 'No pudimos leer los impedimentos. Recarga la página.'
/** De QUÉ proceso es lo que se está viendo (F4-2). La tarjeta sigue al selector
 *  de arriba, y decirlo es lo que hace que la pantalla se lea como un solo
 *  sistema en vez de dos listas sin relación. */
function previewScopeLine(processName: string): string {
  return `Estás viendo el proceso "${processName}".`
}
/** El proceso existe, pero la vista previa no lo trae: hoy no puede pasar
 *  (ningún espacio tiene conexiones y se toman todos), y en cuanto se elija qué
 *  atiende cada canal va a pasar todos los días. Se dice, en vez de dibujar una
 *  tarjeta vacía que se lee como un error. */
const PREVIEW_PROCESS_ABSENT =
  'Este proceso todavía no está conectado a ningún canal, así que no aparece en la vista previa.'
/** A dónde va a parar la conversación de un proceso, en palabras del cliente. */
const PREVIEW_TERMINAL_LABEL: Record<AgentTerminal, string> = {
  pasar: 'Termina con una persona',
  cierre: 'Termina con una despedida',
  fin_ok: 'Termina sin pasar a una persona',
}

/** Las dos vistas, con el nombre que lleva su botón. */
const VIEW_STRUCTURE = 'Estructura'
const VIEW_EXAMPLE = 'Conversación de ejemplo'

/** Qué corre en cada etapa, dicho en la vista previa. Es lo que separa lo que el
 *  agente HACE de lo que el proceso declara: una etapa que el agente no corre se
 *  dibuja igual, con su renglón diciendo por qué.
 *
 *  POR TRAMOS (F3-1d). Ya no existe "hoy sólo pregunta en la primera etapa de
 *  captura": el compilador recorre TODAS las columnas de captura de la ventana,
 *  una por tramo. Quedan dos hechos que sí hay que decir, y son distintos entre
 *  sí: un tramo que compila y no pregunta nada (sólo adelanta la tarjeta), y una
 *  columna de captura que queda FUERA de la ventana (después del traspaso o del
 *  fin, o después de la última que declara algo), a donde el agente no llega. */
const STAGE_NOTE_BARE_ADVANCE = 'Avanza la tarjeta aquí; esta etapa no declara datos.'
const STAGE_NOTE_OUT_OF_WINDOW =
  'El agente no llega a esta etapa: va después de donde termina su recorrido.'
const STAGE_NOTE_MANUAL = 'La llena una persona desde la tarjeta del prospecto.'
const STAGE_NOTE_HANDOFF = 'La conversación pasa con una persona.'
const STAGE_NOTE_END = 'Termina el proceso.'
const STAGE_NOTE_UNSUPPORTED = 'Todavía no lo maneja el agente.'
/** LO QUE PASA EN LA ETAPA QUE AGENDA (D22-9). Hasta aquí caía en la respuesta
 *  de abajo — "Todavía no lo maneja el agente." — y desde D22-5/D22-6 eso es
 *  falso: la columna compila, el tramo de reserva busca disponibilidad real,
 *  ofrece el horario más cercano y reserva. Se dice ENTERO porque la etapa hace
 *  tres cosas seguidas y nombrar sólo la última deja al Dueño sin saber con qué
 *  se busca. */
const STAGE_NOTE_AGENDA =
  'El Agente consulta los horarios de tus doctores, ofrece los más cercanos y agenda la cita.'

/** EL AVISO DE LA ETAPA QUE ATIENDE UNA PERSONA.
 *
 *  No es una advertencia de estilo: es el runtime. Entrar a una etapa cuyo
 *  responsable es una persona o un equipo entrega la conversación ENTERA a un
 *  humano (`stagedHandoff`), así que una captura que compila y la atiende una
 *  persona corta el recorrido por etapas justo ahí, y los tramos siguientes no
 *  se preguntan nunca. Sin decirlo, la tarjeta dibuja un recorrido completo que
 *  el prospecto no va a vivir. */
const STAGE_OWNER_WARNING =
  'Esta etapa la atiende una persona: al llegar aquí la conversación pasa a manos humanas y el ' +
  'agente ya no pregunta lo que sigue.'

/** NINGUNA columna declaró datos, así que la captura se cae a la ficha completa
 *  en la primera (regla de PROCESO, no de etapa). Se dice UNA vez arriba del
 *  proceso, y con la salida: declarar los datos en las etapas es lo que hace que
 *  el agente siga el recorrido que el cliente dibujó. */
const PROCESS_WHOLE_FICHA_FALLBACK =
  'Ninguna etapa declara datos: se pregunta la ficha completa en la primera etapa de captura. ' +
  'Decláralo en las etapas para que el agente siga tu recorrido.'

/** La honestidad del ejemplo, dicha ARRIBA del hilo y no debajo: quien lo lea
 *  tiene que saber qué está leyendo antes de leerlo. */
const EXAMPLE_NOTE =
  'Es un ejemplo armado con las palabras que el agente tiene escritas. Lo que dice la persona es ' +
  'una descripción de lo que querría, no un mensaje real.'
/** D6: el aviso de privacidad no es un turno del flujo, es la puerta del
 *  pipeline. Se marca como lo que pasa antes, sin inventarle texto. */
const EXAMPLE_CONSENT_SEPARATOR = 'Antes de todo: el aviso de privacidad, según tu configuración'
const EXAMPLE_PERSON_AUTHOR = 'La persona'

/** Cada cuánto se vuelve a escribir "Leído hace…". Medio minuto: suficiente para
 *  que el renglón no envejezca a la vista, y sin repintar la tarjeta a cada rato. */
const READ_AT_TICK_MS = 30_000

function dataTypeLabel(dataType: string | null): string | null {
  if (dataType === null) return null
  return DATA_TYPE_LABELS[dataType as FieldDataType] ?? null
}

/** El renglón que dice qué pasa en una etapa, o `null` cuando lo que pasa son
 *  las preguntas que se listan debajo. */
function stageNoteOf(stage: PreviewStage): string | null {
  switch (stage.behavior) {
    case 'captura':
      // Fuera de la ventana: el agente no llega. Dentro y con preguntas: hablan
      // las preguntas, no un renglón que las repita. Dentro y sin ninguna: es un
      // tramo que sólo adelanta la tarjeta, y eso sí hay que decirlo.
      if (!stage.compiles) return STAGE_NOTE_OUT_OF_WINDOW
      return stage.asks.length > 0 ? null : STAGE_NOTE_BARE_ADVANCE
    case 'agenda':
      // MISMA ventana que la captura: una columna que agenda pero queda después
      // del traspaso o del fin no la alcanza el agente, y decirle que reserva
      // sería la mentira contraria. Dentro de la ventana se dice lo que hace,
      // ADEMÁS de las preguntas que cuelgan debajo: el tramo pregunta lo suyo y
      // después reserva, así que el renglón no repite las preguntas, las
      // completa.
      return stage.compiles ? STAGE_NOTE_AGENDA : STAGE_NOTE_OUT_OF_WINDOW
    case 'llenado_manual':
      return STAGE_NOTE_MANUAL
    case 'handoff':
      return STAGE_NOTE_HANDOFF
    case 'fin':
      return STAGE_NOTE_END
    default:
      return STAGE_NOTE_UNSUPPORTED
  }
}

export interface AgentPreviewCardProps {
  /** El agente que se está previendo. */
  botId: string
  /** El proceso SELECCIONADO arriba: la clave con la que el compilador lo nombra
   *  (`pipeline.key`). La tarjeta dibuja ése y ninguno más. */
  processKey: string
  /** Su nombre, para decir de qué proceso es esto sin buscarlo en la carga. */
  processName: string
}

export function AgentPreviewCard({
  botId,
  processKey,
  processName,
}: AgentPreviewCardProps): JSX.Element {
  const agentPreview = useQuery({
    queryKey: ['agent-compiled-preview', botId],
    queryFn: () => getAgentPreview(botId),
    enabled: botId !== '',
    // Sólo lectura: la vuelven a pedir las pantallas que guardan procesos,
    // etapas o ficha, invalidando esta llave. Nadie la refresca a mano.
    staleTime: 60_000,
  })
  // LA PUERTA DEL PUBLICAR, en su propia consulta y no colgada del clic. Lo que
  // impide publicar tiene que leerse ANTES de apretar: un botón que se ve
  // disponible y contesta con una razón al abrirse es un botón que mintió. Corre
  // al lado de la vista previa, y quien invalida la llave de aquélla invalida
  // TAMBIÉN ésta (los guardados de Procesos, la ficha y la biblioteca lo hacen
  // explícitamente): las dos describen el mismo agente, y dejar una vieja es el
  // botón diciendo "ya refleja tus procesos" sobre un agente que acaba de
  // cambiar.
  const publishPreview = useQuery({
    queryKey: ['agent-publish-preview', botId],
    queryFn: () => getPublishPreview(botId),
    enabled: botId !== '',
    staleTime: 30_000,
  })
  const qc = useQueryClient()
  // EL DIÁLOGO, con sus tres momentos: lo que cambiaría, lo que quedó, y lo
  // deshecho. Uno solo, como el de la captura por proceso: son una decisión.
  const [publishOpen, setPublishOpen] = useState(false)
  const [publishBusy, setPublishBusy] = useState(false)
  const [publishResult, setPublishResult] = useState<PublishAgentResult | null>(null)
  // La razón del servidor cuando refusa (409), dicha DENTRO del diálogo: es lo
  // que ocupa el lugar del recibo que no hubo.
  const [publishRefusal, setPublishRefusal] = useState('')
  // Lo que el servidor dice que deshacer hizo. Es un ESTADO del diálogo y no un
  // toast que se va: cerrar sobre un cambio real se ve igual que no haber
  // apretado nada.
  const [publishUndone, setPublishUndone] = useState('')

  const [view, setView] = useState<'structure' | 'example'>('structure')
  // La edad se cuenta desde `dataUpdatedAt`, nunca desde un `Date.now()` leído
  // en el render: el reloj de la pantalla avanza aquí, y sólo aquí.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), READ_AT_TICK_MS)
    return () => clearInterval(id)
  }, [])

  const data = agentPreview.data
  // El proceso seleccionado, y su ejemplo. Ausente es un estado real y distinto
  // de "todavía no carga": el proceso existe en la pantalla y no en el agente.
  const process = data?.preview.processes.find((p) => p.key === processKey)
  const example = data?.preview.example.processes.find((p) => p.key === processKey)

  // LOS IMPEDIMENTOS, tal como llegaron. "No hay nada que publicar" es uno de
  // ellos y lo manda el servidor: componerlo aquí sería la pantalla decidiendo
  // por su cuenta que el agente está al día.
  const blockers = publishPreview.data?.blockers ?? []
  const firstBlocker = blockers[0]
  // "No hay nada que publicar" NO es una advertencia: es el estado bueno. Se
  // pinta aparte y en tono neutro; los demás impedimentos sí piden algo.
  const warnBlockers = blockers.filter((b) => b.code !== 'nothing_to_publish')
  const quietBlocker = blockers.find((b) => b.code === 'nothing_to_publish')
  const publishIdentity = publishPreview.data?.identity ?? null
  // El delta que el diálogo enseña: el de la vista previa antes de publicar, y
  // el que el servidor devolvió después. Los dos salen del mismo cálculo.
  const publishDelta: AgentDelta | undefined = publishResult
    ? publishResult.delta
    : publishPreview.data?.delta

  /** Lo que las dos escrituras vuelven a preguntar. La vista previa compilada y
   *  la del publicar describen el mismo agente, y la tira de arriba lee los
   *  flujos del agente para saber cuál es el principal: publicar los mueve los
   *  tres, así que los tres se invalidan juntos o la pantalla queda contando un
   *  agente que ya no existe. */
  async function refreshAfterWrite(): Promise<void> {
    await qc.invalidateQueries({ queryKey: ['agent-compiled-preview', botId] })
    await qc.invalidateQueries({ queryKey: ['agent-publish-preview', botId] })
    await qc.invalidateQueries({ queryKey: ['worker-flows', botId] })
  }

  function openPublish(): void {
    setPublishResult(null)
    setPublishRefusal('')
    setPublishUndone('')
    setPublishOpen(true)
  }

  function closePublish(): void {
    setPublishOpen(false)
    setPublishResult(null)
    setPublishRefusal('')
    setPublishUndone('')
  }

  async function submitPublish(): Promise<void> {
    if (botId === '') return
    setPublishBusy(true)
    setPublishRefusal('')
    try {
      setPublishResult(await publishAgent(botId, publishPreview.data?.expectedVersion))
      await refreshAfterWrite()
    } catch (err) {
      setPublishRefusal(apiErrorMessage(err, 'No pudimos publicar el agente.'))
    } finally {
      setPublishBusy(false)
    }
  }

  /** Deshace ESTA publicación, no la anterior: el conjunto que se acaba de
   *  escribir es el único que el servidor deja deshacer, y es el que el cliente
   *  está mirando. Contesta en el diálogo, con la frase del servidor. */
  async function submitPublishUndo(): Promise<void> {
    if (botId === '' || publishResult === null) return
    setPublishBusy(true)
    try {
      setPublishUndone(await undoPublish(botId, publishResult.compilationId))
      await refreshAfterWrite()
    } catch (err) {
      setPublishRefusal(apiErrorMessage(err, 'No pudimos deshacer la publicación.'))
    } finally {
      setPublishBusy(false)
    }
  }

  return (
    <>
      <div
        className="rounded-xl border border-line bg-surface p-5"
        data-testid="agent-compiled-preview"
      >
        {/* EL ENCABEZADO Y LA ACCIÓN, en el mismo renglón: lo que la tarjeta
          describe y el botón que lo escribe. El botón va FUERA del grupo de las
          dos vistas de abajo, que es un control de lectura y no una acción:
          juntos se leerían como tres maneras de mirar lo mismo. */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[13px] font-semibold text-text">Así quedaría tu agente</h2>
            <p className="mt-1.5 text-[11.5px] text-text-3">{PREVIEW_INTRO}</p>
            {processName !== '' && (
              <p className="mt-1 text-[11.5px] text-text-3" data-testid="agent-preview-scope">
                {previewScopeLine(processName)}
              </p>
            )}
            {agentPreview.dataUpdatedAt > 0 && (
              <p className="mt-1 text-[11.5px] text-text-3" data-testid="agent-preview-read-at">
                Leído {relativeAgeEs(agentPreview.dataUpdatedAt, now)}
              </p>
            )}
          </div>
          <div className="flex min-w-0 flex-col items-start gap-1.5">
            {/* DESHABILITADO CON SU RAZÓN ENCIMA. Un botón apagado y mudo manda a
              la gente a buscar un permiso que ya tiene; la primera razón viaja
              en el `title` y la lista entera se lee debajo de la tarjeta. */}
            <Button
              data-testid="agent-publish-button"
              disabled={botId === '' || publishPreview.data === undefined || blockers.length > 0}
              title={firstBlocker?.message}
              onClick={openPublish}
            >
              {PUBLISH_LABEL}
            </Button>
            {/* La puerta a probar, junto al Publicar: mirar, probar y escribir
              son los tres verbos de la misma tarjeta. Enlace y no botón, porque
              se va a OTRA pantalla (el editor del agente, con su simulador
              abierto) en vez de actuar aquí. */}
            {botId !== '' && (
              <Link
                to={tryHref(botId)}
                data-testid="agent-try-link"
                className="text-[11.5px] font-semibold text-link hover:underline"
              >
                {TRY_LINK_LABEL}
              </Link>
            )}
            {publishIdentity !== null && (
              <p
                className="text-[11.5px] text-text-3 [overflow-wrap:anywhere]"
                data-testid="agent-publish-identity"
              >
                {publishIdentityLine(publishIdentity.label)}
              </p>
            )}
          </div>
        </div>

        {/* POR QUÉ NO SE PUEDE PUBLICAR, entero y en las frases del servidor. La
          lista completa y no sólo la primera: quien mira la pantalla quiere
          saber TODO lo que le falta, y el POST ya refusa con una sola cosa que
          hacer para quien apretó. */}
        {blockers.length > 0 && (
          <div className="mt-3 space-y-1.5" data-testid="agent-publish-blockers">
            {warnBlockers.length > 0 && (
              <Alert tone="warn">
                <ul className="space-y-1" data-testid="agent-publish-blockers-warn">
                  {warnBlockers.map((b) => (
                    <li key={b.code} data-testid="agent-publish-blocker">
                      {b.message}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
            {quietBlocker && (
              <p className="text-[12px] text-text-3" data-testid="agent-publish-blocker">
                {quietBlocker.message}
              </p>
            )}
          </div>
        )}
        {/* LA PUERTA QUE NO SE PUDO LEER. El botón se apaga porque no hay datos,
          y un botón apagado sin una palabra es una puerta pintada: se dice qué
          pasó y qué hacer, en tono neutro (el fallo es nuestro, no del agente). */}
        {publishPreview.isError && (
          <p className="mt-3 text-[12px] text-text-3" data-testid="agent-publish-preview-error">
            {PUBLISH_PREVIEW_ERROR}
          </p>
        )}

        {/* LA VISTA PREVIA QUE NO CARGÓ (restaurado en F4-10). La reescritura de
          F4-2 se llevó este estado y el fallo quedaba mudo: la tarjeta pintaba
          su encabezado y nada más, que se lee como un proceso sin nada dentro en
          vez de como un error nuestro. */}
        {agentPreview.isError && (
          <div className="mt-3" data-testid="agent-preview-error">
            <Alert tone="warn">
              {apiErrorMessage(agentPreview.error, 'No se pudo cargar la vista previa.')}
            </Alert>
          </div>
        )}

        {data && !process && (
          <p className="mt-3 text-[12px] text-text-2" data-testid="agent-preview-process-absent">
            {PREVIEW_PROCESS_ABSENT}
          </p>
        )}

        {data && process && (
          <div className="mt-4 flex flex-col gap-4">
            {/* Las dos maneras de leer el MISMO proceso. Son los únicos botones de
              la tarjeta: aquí no se aplica nada. */}
            <div
              role="group"
              aria-label="Cómo ver la vista previa"
              className="flex flex-wrap gap-1.5"
            >
              <Button
                size="sm"
                variant={view === 'structure' ? 'secondary' : 'ghost'}
                aria-pressed={view === 'structure'}
                onClick={() => setView('structure')}
              >
                {VIEW_STRUCTURE}
              </Button>
              <Button
                size="sm"
                variant={view === 'example' ? 'secondary' : 'ghost'}
                aria-pressed={view === 'example'}
                onClick={() => setView('example')}
              >
                {VIEW_EXAMPLE}
              </Button>
            </div>

            {view === 'structure' ? (
              <div
                className="rounded-lg border border-line p-3"
                data-testid="agent-preview-process"
              >
                <p className="text-[12.5px] font-medium text-text">{process.optionLabel}</p>
                <p className="mt-0.5 text-[11.5px] text-text-3">{process.name}</p>
                {/* La caída a la ficha completa es del PROCESO, no de una etapa: se
                  dice una vez, arriba, o las columnas de abajo se leen como si el
                  compilador las hubiera ignorado. */}
                {process.wholeFichaFallback && (
                  <p
                    className="mt-1.5 text-[11.5px] text-text-3"
                    data-testid="agent-preview-fallback"
                  >
                    {PROCESS_WHOLE_FICHA_FALLBACK}
                  </p>
                )}
                <ul className="mt-2 flex flex-col gap-2">
                  {process.stages.map((stage) => {
                    const note = stageNoteOf(stage)
                    return (
                      <li
                        key={stage.key}
                        className="rounded-md bg-surface-2 p-2.5"
                        data-testid="agent-preview-stage"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[12px] font-medium text-text [overflow-wrap:anywhere]">
                            {stage.name}
                          </span>
                          <Chip tone={STAGE_BEHAVIOR_TONE[stage.behavior]}>
                            {STAGE_BEHAVIOR_CHIP[stage.behavior]}
                          </Chip>
                        </div>
                        {stage.requires.length > 0 && (
                          <p
                            className="mt-1 text-[11.5px] text-text-3"
                            data-testid="agent-preview-stage-requires"
                          >
                            Requiere: {stage.requires.join(', ')}
                          </p>
                        )}
                        {stage.obtains.length > 0 && (
                          <p
                            className="mt-0.5 text-[11.5px] text-text-3"
                            data-testid="agent-preview-stage-obtains"
                          >
                            Obtiene: {stage.obtains.join(', ')}
                          </p>
                        )}
                        {note !== null && (
                          <p
                            className="mt-1 text-[11.5px] text-text-3"
                            data-testid="agent-preview-stage-note"
                          >
                            {note}
                          </p>
                        )}
                        {stage.asks.length > 0 && (
                          <ol className="mt-1.5 flex flex-col gap-1.5">
                            {stage.asks.map((ask) => {
                              const type = dataTypeLabel(ask.dataType)
                              return (
                                <li
                                  key={ask.key}
                                  className="text-[12px] text-text-2"
                                  data-testid="agent-preview-question"
                                >
                                  <span className="[overflow-wrap:anywhere]">
                                    {ask.label}
                                    {type !== null && (
                                      <span className="text-text-3">
                                        {' · '}
                                        {type}
                                      </span>
                                    )}
                                  </span>
                                  {ask.prompt !== '' && (
                                    <p
                                      className="mt-0.5 text-[11.5px] italic text-text-3 [overflow-wrap:anywhere]"
                                      data-testid="agent-preview-prompt"
                                    >
                                      {`"${ask.prompt}"`}
                                    </p>
                                  )}
                                  {/* De dónde sale su lista (D21-3). La frase
                                    llega redactada del servidor: aquí sólo se
                                    pinta, sin comillas, porque no es algo que
                                    el agente diga. */}
                                  {ask.sourceNote !== null && (
                                    <p
                                      className="mt-0.5 text-[11.5px] text-text-3 [overflow-wrap:anywhere]"
                                      data-testid="agent-preview-source-note"
                                    >
                                      {ask.sourceNote}
                                    </p>
                                  )}
                                </li>
                              )
                            })}
                          </ol>
                        )}
                        {/* El runtime, no un consejo: entrar a una etapa cuyo
                          responsable es una persona entrega la conversación
                          entera (`stagedHandoff`), así que el recorrido se corta
                          aquí. */}
                        {stage.compiles && stage.owner !== 'bot' && (
                          <div className="mt-1.5" data-testid="agent-preview-owner-warning">
                            <Alert tone="warn">{STAGE_OWNER_WARNING}</Alert>
                          </div>
                        )}
                      </li>
                    )
                  })}
                </ul>
                <p className="mt-2 text-[11.5px] text-text-3">
                  {PREVIEW_TERMINAL_LABEL[process.terminal]}
                </p>
              </div>
            ) : (
              <>
                <p
                  className="rounded-xl border border-line bg-surface-2 p-3 text-[12px] text-text-2"
                  data-testid="agent-preview-example-note"
                >
                  {EXAMPLE_NOTE}
                </p>
                <MessageThread label={VIEW_EXAMPLE}>
                  <DaySeparator label={EXAMPLE_CONSENT_SEPARATOR} />
                  {example && (
                    <>
                      <DaySeparator label={process.name} />
                      {example.turns.map((turn, i) => {
                        const key = `${example.key}-${i}`
                        if (turn.kind === 'event') {
                          const terminal = turn.text as AgentTerminal
                          return (
                            <SystemEvent
                              key={key}
                              kind={terminal === 'pasar' ? 'handoff' : 'resolved'}
                              text={PREVIEW_TERMINAL_LABEL[terminal]}
                            />
                          )
                        }
                        if (turn.kind === 'says') {
                          return (
                            <MessageBubble key={key} sender="bot">
                              {turn.text}
                            </MessageBubble>
                          )
                        }
                        return (
                          <MessageBubble key={key} sender="user" author={EXAMPLE_PERSON_AUTHOR}>
                            {turn.kind === 'answers' ? `Responde: ${turn.text}` : turn.text}
                          </MessageBubble>
                        )
                      })}
                    </>
                  )}
                </MessageThread>
              </>
            )}
          </div>
        )}
      </div>

      {/* PUBLICAR EL AGENTE (W4'-3b). UN diálogo con tres momentos: lo que
        cambiaría antes de escribir, lo que quedó escrito después con Deshacer,
        y lo deshecho. No se parte en pantallas porque es una sola decisión, y
        es el mismo patrón que tenía el diálogo de la captura por proceso
        (retirado en W4'-4b): dos diálogos distintos para dos publicaciones del
        mismo agente se leerían como dos productos.

        El subtítulo va vacío cuando no hay canal: '' es lo que el Modal entiende
        por "no lo dibujes", y `undefined` no compila con
        `exactOptionalPropertyTypes`. */}
      <Modal
        open={publishOpen}
        onClose={closePublish}
        title={PUBLISH_DIALOG_TITLE}
        subtitle={publishIdentity === null ? '' : publishIdentityLine(publishIdentity.label)}
        busy={publishBusy}
        footer={
          publishUndone !== '' ? (
            <Button size="sm" className="ml-auto" disabled={publishBusy} onClick={closePublish}>
              Listo
            </Button>
          ) : publishResult ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                disabled={publishBusy}
                onClick={() => void submitPublishUndo()}
              >
                Deshacer
              </Button>
              <Button size="sm" className="ml-auto" disabled={publishBusy} onClick={closePublish}>
                Listo
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" size="sm" disabled={publishBusy} onClick={closePublish}>
                Cancelar
              </Button>
              <Button
                size="sm"
                className="ml-auto"
                loading={publishBusy}
                disabled={publishBusy}
                onClick={() => void submitPublish()}
              >
                {PUBLISH_CONFIRM_LABEL}
              </Button>
            </>
          )
        }
      >
        {publishUndone !== '' ? (
          // El tercer momento: lo deshecho, con la frase del servidor y sin
          // ofrecer Deshacer otra vez sobre algo que ya no está.
          <p className="text-[12.5px] leading-relaxed text-text" data-testid="agent-publish-undone">
            {publishUndone}
          </p>
        ) : (
          <div className="space-y-3">
            {publishResult && (
              <FactList
                heading={PUBLISH_RECEIPT_HEADING}
                facts={publishResult.facts}
                testId="agent-publish-receipt"
                factTestId="agent-publish-receipt-fact"
              />
            )}
            {/* LO QUE CAMBIA, en las frases del servidor. La MISMA lista antes y
              después de escribir: lo único que se conjuga es el encabezado. Se
              dibuja con `FactList`, como el recibo de arriba: dos maneras de
              dibujar la misma lista en el mismo diálogo serían dos productos. */}
            {publishDelta && publishDelta.summary.length > 0 && (
              <FactList
                heading={publishResult ? PUBLISH_DELTA_PAST_HEADING : PUBLISH_DELTA_FUTURE_HEADING}
                facts={publishDelta.summary}
                testId="agent-publish-delta"
                factTestId="agent-publish-delta-item"
              />
            )}
            {publishResult && (
              <p className="text-[11.5px] leading-relaxed text-text-3">{PUBLISH_UNDO_HINT}</p>
            )}
            {/* LA RAZÓN DEL SERVIDOR cuando refusa, donde habría ido el recibo:
              es exactamente lo que ocupa su lugar. */}
            {publishRefusal !== '' && (
              <p
                className="text-[12.5px] leading-relaxed text-sens"
                data-testid="agent-publish-refusal"
              >
                {publishRefusal}
              </p>
            )}
          </div>
        )}
      </Modal>
    </>
  )
}
